import * as PIXI from 'pixi.js';

import { AlphaAttribute, ColorAttribute } from '../attributes';
import type { Resolution, DestroyOptions } from '../../types';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A ColorAsset Object will load the specified solid color.
 */
export class ColorAsset extends BaseAsset {
    override _classType = 'ColorAsset';

    #source: PIXI.Sprite = null;
    #mediaTargetResolution: Resolution = undefined;

    @ColorAttribute()
    accessor sourceColor = 0; // black
    @AlphaAttribute()
    accessor sourceAlpha = 1.0; // fully opaque

    constructor(container, settings: BaseAssetSettings) {
        super(container, settings);
        this.#mediaTargetResolution = Object.assign({}, this.root.referenceResolution);
    }

    /**
     * The resolution that best fits the media into the current slide.  This is usually either root resolution, or
     * the resolution of the resulting visible area when media is masked.
     */
    get mediaTargetResolution(): Resolution {
        return this.#mediaTargetResolution;
    }

    set mediaTargetResolution(resolution: Resolution) {
        this.#mediaTargetResolution.width = resolution.width;
        this.#mediaTargetResolution.height = resolution.height;

        if (this.#source === null) {
            return;
        }

        this.applyMediaTargetResolution();
    }

    applyMediaTargetResolution() {
        if (!this.#mediaTargetResolution) {
            return;
        }
        this.width = this.#mediaTargetResolution.width;
        this.height = this.#mediaTargetResolution.height;
    }

    override _build() {
        this.clearAnimation();
        if (this.#source) {
            this.#source.destroy();
            this.#source = null;
        }
        this.#source = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._addInternalChild(this.#source);

        // set target resolution
        this.applyMediaTargetResolution();

        this.applyAnimation();
        this.applyIntroAnimations();
        this.applyOutroAnimations();
    }

    override _draw() {
        if (!this.#source) {
            return;
        }
        this.#source.tint = this.sourceColor;
        this.#source.alpha = this.sourceAlpha;
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#source = null;
    }

    override get failedToLoad(): boolean {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get shouldLoad() {
        return true;
    }

    override applyTransformation() {
        // override in child class
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['mask', 'sourceColor', 'sourceAlpha']);
    }
}
